import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { parseAppLocation, socialStudioPath } from '../routing/appRoutes';
import { format } from 'date-fns';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Drawer,
  Fab,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Article as ArticleIcon,
  Close as CloseIcon,
  Movie as MovieIcon,
} from '@mui/icons-material';
import { NotebooksContext, FrontendNotebook } from '../contexts/NotebooksContext';
import { ApiError, NotFoundError } from '../errors/ApiError';
import { SocialCampaign, addCampaignPost, createCampaign, listCampaigns } from '../api/socialStudio';
import { CampaignClusterSocialRail } from '../components/socialStudio/CampaignClusterSocialRail';
import { CampaignPostCalendar } from '../components/socialStudio/CampaignPostCalendar';

function isNotFoundError(e: unknown): boolean {
  if (e instanceof NotFoundError) return true;
  if (e instanceof ApiError && e.status === 404) return true;
  if (e instanceof Error && /not found|404/i.test(e.message)) return true;
  return false;
}

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

/** Backend may store draft timestamps as Unix seconds or milliseconds. */
function toUnixMs(ts: number | undefined): number | null {
  if (ts == null || ts <= 0) return null;
  return ts < 1e12 ? ts * 1000 : ts;
}

/** Articles ready to publish, scheduled, or published within the last 7 days (same signals as Distribution Hub + recency). */
function isEligibleLibraryArticle(nb: FrontendNotebook): boolean {
  const d = nb.draft;
  if (!d) return false;

  const ready =
    Boolean(d.ready_for_publishing) &&
    (!d.publication_status || d.publication_status === 'ready_to_publish');
  if (ready) return true;

  if (d.publication_status === 'scheduled') return true;

  if (d.publication_status === 'published') {
    const ms = toUnixMs(d.published_at);
    if (ms == null) return false;
    return Date.now() - ms <= MS_PER_WEEK;
  }

  return false;
}

function articlePickerHint(nb: FrontendNotebook): string {
  const d = nb.draft;
  if (!d) return '';
  if (d.publication_status === 'scheduled') {
    const t = toUnixMs(d.scheduled_publish_at);
    return t ? `Scheduled · ${format(t, 'MMM d, yyyy · p')}` : 'Scheduled';
  }
  if (d.publication_status === 'published') {
    const t = toUnixMs(d.published_at);
    return t ? `Published · ${format(t, 'MMM d, yyyy')}` : 'Published';
  }
  if (d.ready_for_publishing) return 'Ready to publish';
  return '';
}

function sortEligibleArticles(a: FrontendNotebook, b: FrontendNotebook): number {
  const da = a.draft;
  const db = b.draft;
  const aPub = da?.publication_status === 'published';
  const bPub = db?.publication_status === 'published';
  if (aPub !== bPub) return aPub ? 1 : -1;
  if (!aPub && !bPub) {
    const la = a.draft?.title?.trim() || a.name || '';
    const lb = b.draft?.title?.trim() || b.name || '';
    return la.localeCompare(lb, undefined, { sensitivity: 'base' });
  }
  const ta = toUnixMs(da?.published_at) ?? 0;
  const tb = toUnixMs(db?.published_at) ?? 0;
  return tb - ta;
}

function postSummary(c: SocialCampaign): string {
  const posts = c.posts || [];
  if (posts.length === 0) return 'No posts yet';
  const scheduled = posts.filter((p) => p.status === 'scheduled').length;
  const published = posts.filter((p) => p.status === 'published').length;
  const parts = [`${posts.length} post${posts.length === 1 ? '' : 's'}`];
  if (scheduled) parts.push(`${scheduled} scheduled`);
  if (published) parts.push(`${published} published`);
  return parts.join(' · ');
}

function articleLabel(n: FrontendNotebook): string {
  return n.draft?.title?.trim() || n.name || 'Untitled';
}

const cardListSx = {
  bgcolor: '#1c1c22',
  border: '1px solid #3f3f46',
  color: '#f4f4f5',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  '&:hover': { borderColor: '#6366f1', boxShadow: '0 4px 24px rgba(99, 102, 241, 0.15)' },
};

const inputDarkSx = {
  '& .MuiOutlinedInput-root': {
    color: '#f4f4f5',
    '& fieldset': { borderColor: '#52525b' },
    '&:hover fieldset': { borderColor: '#71717a' },
    '&.Mui-focused fieldset': { borderColor: '#818cf8' },
  },
  '& .MuiInputLabel-root': { color: '#d4d4d8' },
  '& .MuiSvgIcon-root': { color: '#a1a1aa' },
};

const LayoutSocialStudio: React.FC = () => {
  const { notebooks, getActiveNotebooks, fetchNotebooks } = useContext(NotebooksContext);
  const library = useMemo(() => getActiveNotebooks(), [getActiveNotebooks]);

  const eligibleArticles = useMemo(
    () => library.filter(isEligibleLibraryArticle).sort(sortEligibleArticles),
    [library]
  );

  const [campaigns, setCampaigns] = useState<SocialCampaign[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectedCampaign = useMemo(
    () => (selectedId ? campaigns.find((c) => c.id === selectedId) : undefined),
    [campaigns, selectedId]
  );

  const referenceNotebook = useMemo(() => {
    if (!selectedCampaign || selectedCampaign.referenceType !== 'notebook' || !selectedCampaign.referenceId)
      return undefined;
    return notebooks.find((n) => n.id === selectedCampaign.referenceId);
  }, [selectedCampaign, notebooks]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [clusterDrawerOpen, setClusterDrawerOpen] = useState(false);
  /** choose: pick path | content: form from library */
  const [drawerMode, setDrawerMode] = useState<'choose' | 'content'>('choose');

  const [newNotebookId, setNewNotebookId] = useState('');
  const [newTitle, setNewTitle] = useState('');
  /** Avoid overwriting a manually edited title when the notebook list refetches. */
  const lastDefaultTitleForArticleIdRef = useRef<string | null>(null);

  const openNewCampaign = () => {
    setDrawerMode('choose');
    setNewNotebookId('');
    setNewTitle('');
    setError(null);
    setDrawerOpen(true);
  };

  useEffect(() => {
    if (drawerMode !== 'content') return;
    if (newNotebookId && !eligibleArticles.some((n) => n.id === newNotebookId)) {
      setNewNotebookId('');
    }
  }, [drawerMode, eligibleArticles, newNotebookId]);

  useEffect(() => {
    if (drawerMode !== 'content') return;
    if (!newNotebookId) {
      setNewTitle('');
      lastDefaultTitleForArticleIdRef.current = null;
      return;
    }
    const nb = eligibleArticles.find((n) => n.id === newNotebookId);
    if (!nb) return;
    if (lastDefaultTitleForArticleIdRef.current === newNotebookId) return;
    lastDefaultTitleForArticleIdRef.current = newNotebookId;
    setNewTitle(articleLabel(nb));
  }, [newNotebookId, drawerMode, eligibleArticles]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerMode('choose');
  };

  const refreshList = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const rows = await listCampaigns();
      setCampaigns(rows);
      setSelectedId((prev) => {
        if (rows.length === 0) return null;
        if (prev && rows.some((r) => r.id === prev)) return prev;
        return null;
      });
    } catch (e) {
      if (isNotFoundError(e)) {
        setCampaigns([]);
        setSelectedId(null);
      } else {
        setError(e instanceof Error ? e.message : 'Failed to load campaigns');
      }
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  useEffect(() => {
    if (clusterDrawerOpen && !selectedCampaign) setClusterDrawerOpen(false);
  }, [clusterDrawerOpen, selectedCampaign]);

  // ---- URL sync: /social-studio/:campaignId opens that campaign's drawer (see routing/appRoutes.ts) ----
  const navigate = useNavigate();
  const location = useLocation();
  const routeLocation = parseAppLocation(location.pathname);
  const routeCampaignId = routeLocation?.view === 'social_studio' ? routeLocation.itemId : undefined;
  const openCampaignId = clusterDrawerOpen && selectedId ? selectedId : null;
  /** Campaign id from a deep link that hasn't been opened yet (waiting for the list to load). */
  const pendingCampaignIdRef = useRef<string | null>(null);

  // State → URL
  const prevOpenCampaignIdRef = useRef(openCampaignId);
  useEffect(() => {
    if (prevOpenCampaignIdRef.current === openCampaignId) return;
    prevOpenCampaignIdRef.current = openCampaignId;
    if (pendingCampaignIdRef.current) return;
    const path = socialStudioPath(openCampaignId ?? undefined);
    if (location.pathname !== path) navigate(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react only to drawer/selection changes
  }, [openCampaignId]);

  // URL → state: deep links and back/forward.
  const prevPathnameRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      prevPathnameRef.current = location.pathname;
      pendingCampaignIdRef.current = routeCampaignId ?? null;
      if (!routeCampaignId && openCampaignId) setClusterDrawerOpen(false);
    }
    const id = pendingCampaignIdRef.current;
    if (!id) return;
    if (openCampaignId === id) {
      pendingCampaignIdRef.current = null;
      return;
    }
    if (campaigns.some((c) => c.id === id)) {
      pendingCampaignIdRef.current = null;
      setSelectedId(id);
      setClusterDrawerOpen(true);
    } else if (!loadingList && !error) {
      // (On a load error the list shows it and the URL is kept, so a reload can retry the link.)
      pendingCampaignIdRef.current = null;
      setError("That campaign wasn't found. It may belong to another analytic profile, or it was deleted.");
      navigate(socialStudioPath(), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openCampaignId read at path change only
  }, [location.pathname, campaigns, loadingList, error]);

  const handleCreateFromContent = async () => {
    const title = newTitle.trim();
    if (!newNotebookId || !title) return;
    setError(null);
    try {
      const nb = eligibleArticles.find((n) => n.id === newNotebookId);
      const label = nb ? articleLabel(nb) : '';
      const created = await createCampaign({
        referenceType: 'notebook',
        referenceId: newNotebookId,
        referenceLabel: label,
        title,
      });
      setCampaigns((prev) => [created, ...prev]);
      setSelectedId(created.id);
      setClusterDrawerOpen(true);
      setNewTitle('');
      closeDrawer();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create campaign');
    }
  };

  const startVideoCampaignPath = async () => {
    setError(null);
    try {
      const created = await createCampaign({
        referenceType: 'video',
        title: newTitle.trim() || 'Video campaign',
      });
      setCampaigns((prev) => [created, ...prev]);
      setSelectedId(created.id);
      setClusterDrawerOpen(true);
      setNewTitle('');
      setDrawerOpen(false);
      setDrawerMode('choose');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create campaign');
    }
  };

  const openClusterDrawerForCampaign = (c: SocialCampaign) => {
    setSelectedId(c.id);
    setClusterDrawerOpen(true);
  };

  const closeClusterDrawer = () => {
    setClusterDrawerOpen(false);
  };

  return (
    <Box
      sx={{
        bgcolor: '#0a0a0a',
        minHeight: 'calc(100vh - 80px)',
        color: '#fafafa',
        px: { xs: 2, md: 3 },
        pt: 2,
        pb: 10,
        position: 'relative',
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loadingList ? (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: '#818cf8' }} />
        </Box>
      ) : campaigns.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: '#141418',
            border: '1px dashed #52525b',
            borderRadius: 2,
          }}
        >
          <Typography sx={{ color: '#a1a1aa', mb: 1 }}>
            No marketing campaigns yet.
          </Typography>
          <Typography variant="body2" sx={{ color: '#71717a' }}>
            Use the button below to create one from your library or for video segmentation.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {campaigns.map((c) => (
            <Grid item xs={12} key={c.id}>
              <Card sx={{ ...cardListSx, ...(selectedId === c.id ? { borderColor: '#818cf8', boxShadow: '0 0 0 1px #6366f1' } : {}) }}>
                <CardActionArea onClick={() => openClusterDrawerForCampaign(c)} sx={{ alignItems: 'stretch' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#fafafa', mb: 0.5 }}>
                      {c.title}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                      <Chip
                        size="small"
                        label={postSummary(c)}
                        sx={{ bgcolor: '#27272a', color: '#d4d4d8', border: '1px solid #3f3f46' }}
                      />
                      {c.video?.phase === 'ready' || c.video?.phase === 'segmenting' ? (
                        <Chip
                          size="small"
                          label={c.video.phase === 'ready' ? 'Video ready' : 'Segmenting'}
                          sx={{ bgcolor: '#27272a', color: '#d4d4d8', border: '1px solid #3f3f46' }}
                        />
                      ) : null}
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Drawer
        anchor="right"
        open={clusterDrawerOpen}
        onClose={closeClusterDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: '70vw' },
            maxWidth: '100vw',
            bgcolor: '#0f0f12',
            borderLeft: '1px solid #3f3f46',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            p: 2,
            borderBottom: '1px solid #3f3f46',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 600 }}>
              {selectedCampaign?.title ?? 'Campaign'}
            </Typography>
          </Box>
          <IconButton onClick={closeClusterDrawer} sx={{ color: '#a1a1aa', flexShrink: 0 }} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: { xs: 0, md: 1 }, pb: 2 }}>
          {selectedCampaign ? (
            <CampaignPostCalendar
              campaign={selectedCampaign}
              onUpdated={(next) =>
                setCampaigns((prev) => prev.map((item) => (item.id === next.id ? next : item)))
              }
            />
          ) : null}
          {selectedCampaign?.referenceType === 'notebook' ? (
            <CampaignClusterSocialRail
              notebook={referenceNotebook}
              inDrawer
              onAddToCalendar={(post) => {
                const id = selectedCampaign.id;
                void addCampaignPost(id, post).then((next) => {
                  setCampaigns((prev) => prev.map((item) => (item.id === next.id ? next : item)));
                }).catch((e) => {
                  setError(e instanceof Error ? e.message : 'Could not add this post to the calendar');
                });
              }}
            />
          ) : selectedCampaign?.referenceType === 'video' ? (
            <Box sx={{ px: 2, pt: 2 }}>
              <Typography variant="body2" sx={{ color: '#a1a1aa', lineHeight: 1.6 }}>
                Cluster social posts are tied to library articles. This campaign is video-first — link it to a notebook
                campaign from library content to see cluster-generated posts here, or generate cluster content from the
                Library for that article.
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Drawer>

      <Fab
        color="primary"
        aria-label="New campaign"
        onClick={openNewCampaign}
        sx={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: '#6366f1',
          '&:hover': { bgcolor: '#4f46e5' },
        }}
      >
        <AddIcon />
      </Fab>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 440 },
            maxWidth: '100vw',
            bgcolor: '#141418',
            borderLeft: '1px solid #3f3f46',
            p: 0,
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #3f3f46', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 600 }}>
            {drawerMode === 'choose' && 'New campaign'}
            {drawerMode === 'content' && 'From library content'}
          </Typography>
          <IconButton onClick={closeDrawer} sx={{ color: '#a1a1aa' }} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2.5 }}>
          {drawerMode === 'choose' && (
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                Choose how you want to start.
              </Typography>
              <Card sx={{ ...cardListSx, overflow: 'hidden' }}>
                <CardActionArea
                  onClick={() => {
                    setNewNotebookId('');
                    setNewTitle('');
                    lastDefaultTitleForArticleIdRef.current = null;
                    void fetchNotebooks();
                    setDrawerMode('content');
                  }}
                >
                  <Box
                    sx={{
                      height: 120,
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ArticleIcon sx={{ fontSize: 56, color: 'rgba(255,255,255,0.95)' }} />
                  </Box>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fafafa' }}>
                      From existing content
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5 }}>
                      Create a social media campaign from existing ready-to-publish or published content
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
              <Card sx={{ ...cardListSx, overflow: 'hidden' }}>
                <CardActionArea
                  onClick={() => {
                    void startVideoCampaignPath();
                  }}
                >
                  <Box
                    sx={{
                      height: 120,
                      background: 'linear-gradient(135deg, #0891b2 0%, #2563eb 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <MovieIcon sx={{ fontSize: 56, color: 'rgba(255,255,255,0.95)' }} />
                  </Box>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fafafa' }}>
                      Video segmentation
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a1a1aa', mt: 0.5 }}>
                      Use AI to create a video-first campaign by segmenting your video into relevant clips.
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Stack>
          )}

          {drawerMode === 'content' && (
            <Stack spacing={2}>
              <Button size="small" onClick={() => setDrawerMode('choose')} sx={{ color: '#93c5fd', alignSelf: 'flex-start' }}>
                ← Back
              </Button>
              <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                Name the campaign, then choose an article that is ready to publish, scheduled, or was published in the last 7
                days. The title defaults to the article when you select it.
              </Typography>
              <TextField
                fullWidth
                size="small"
                required
                label="Campaign title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                sx={inputDarkSx}
                helperText="Defaults to the selected article; you can edit it."
                FormHelperTextProps={{ sx: { color: '#71717a', mx: 0 } }}
              />
              <FormControl fullWidth size="small" sx={inputDarkSx}>
                <InputLabel id="ss-nb">Article</InputLabel>
                <Select
                  labelId="ss-nb"
                  label="Article"
                  value={newNotebookId}
                  onChange={(e) => setNewNotebookId(e.target.value as string)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#27272a',
                        border: '1px solid #3f3f46',
                        maxHeight: 360,
                        '& .MuiMenuItem-root': {
                          color: '#f4f4f5',
                          alignItems: 'flex-start',
                          '&:hover': { bgcolor: '#3f3f46' },
                          '&.Mui-selected': { bgcolor: '#3730a3' },
                          '&.Mui-selected:hover': { bgcolor: '#4338ca' },
                        },
                      },
                    },
                  }}
                >
                  {eligibleArticles.map((n) => (
                    <MenuItem key={n.id} value={n.id}>
                      <Box>
                        <Typography variant="body2" component="span" display="block" sx={{ color: '#fafafa' }}>
                          {articleLabel(n)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#a1a1aa' }}>
                          {articlePickerHint(n)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {eligibleArticles.length === 0 && (
                <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                  {library.length === 0
                    ? 'No notebooks in your library yet. Create a draft in Draft Creator first.'
                    : 'No articles match yet. Mark a draft ready to publish, schedule it, or publish it — then try again (or pick an article published in the last 7 days).'}
                </Typography>
              )}
              <Button
                variant="contained"
                fullWidth
                disabled={!newNotebookId || !newTitle.trim()}
                onClick={() => void handleCreateFromContent()}
                sx={{ bgcolor: '#6366f1', py: 1.2 }}
              >
                Create campaign
              </Button>
            </Stack>
          )}

        </Box>
      </Drawer>
    </Box>
  );
};

export default LayoutSocialStudio;
